import { gql } from "@apollo/client";
import { useUpdateContentSharingPermissionMutation } from "@src/features/libraryItem/course/shareButton/AllAdminsAndManagersPermissionRows.generated";
import GeneralAccessSection from "@src/features/libraryItem/course/shareButton/GeneralAccessSection";
import { useToast } from "@src/lib/hooks/useToast";
import { ContentSharingPermissionFragment } from "@src/fragments.generated";
import { GraphqlOperations, PublishState } from "@src/types.generated";
import { FC } from "react";

type Props = {
  courseId?: number;
  moduleId?: number;
  trainingResourceId?: string;
  canEdit: boolean;
  sharingPermissions: ContentSharingPermissionFragment;
  coursePublishState?: PublishState | null;
  commentOptionEnabled?: boolean;
  viewPermissionEnabled?: boolean;
};

const AllAdminsAndManagersPermissionRows: FC<Props> = ({
  courseId,
  moduleId,
  trainingResourceId,
  canEdit,
  sharingPermissions,
  commentOptionEnabled = true,
  viewPermissionEnabled = false,
}) => {
  const { addToast, addErrorToast } = useToast();
  const [updateContentSharingPermission] =
    useUpdateContentSharingPermissionMutation({
      refetchQueries: [
        courseId
          ? GraphqlOperations.Query.CourseSharingPermissions
          : moduleId
            ? GraphqlOperations.Query.ModuleSharingPermissions
            : GraphqlOperations.Query.TrainingResourceSharingPermissions,
      ],
      onCompleted: (data) => {
        if (data?.updateContentSharingPermission?.success) {
          addToast({
            message: "Permission updated!",
          });
        } else {
          addErrorToast({
            ...data,
            callsite: "update-all-managers-or-admins-permission",
          });
        }
      },
    });
  return (
    <GeneralAccessSection
      disabled={!canEdit}
      commentOptionEnabled={commentOptionEnabled}
      viewPermissionEnabled={viewPermissionEnabled}
      allAdminsSelection={sharingPermissions.allAdmins}
      onSetAllAdminsSelection={(newPermission) =>
        canEdit
          ? updateContentSharingPermission({
              variables: {
                input: {
                  courseId,
                  moduleId,
                  trainingResourceId,
                  allAdmins: newPermission,
                },
              },
            })
          : null
      }
      allManagersSelection={sharingPermissions.allManagers}
      onSetAllManagersSelection={(newPermission) =>
        canEdit
          ? updateContentSharingPermission({
              variables: {
                input: {
                  courseId,
                  moduleId,
                  trainingResourceId,
                  allManagers: newPermission,
                },
              },
            })
          : null
      }
    />
  );
};

export default AllAdminsAndManagersPermissionRows;

gql`
  mutation updateContentSharingPermission(
    $input: UpdateContentSharingPermissionInput!
  ) {
    updateContentSharingPermission(input: $input) {
      success
      error {
        code
        message
      }
    }
  }
`;
